import { useEffect, useRef, useState } from 'react';
import { ShieldCheck, ShieldAlert, FileText, Trash2, Upload, ExternalLink } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { INPUT_CLS, LABEL_CLS, ERROR_CLS } from '@/components/brand/BrandKit';
import { UaePassButton } from '@/components/account/UaePassButton';
import { uaepassUnlink, listDocuments, uploadDocument, deleteDocument, documentUrl, apiError } from '@/api/account';
import { toast } from 'sonner';

const CARD = 'rounded-2xl border border-black/5 bg-white p-6 sm:p-8 shadow-[0_16px_40px_rgba(0,0,0,0.05)]';

function Title({ children, sub, badge }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div><h2 className="font-heading font-bold text-lg text-[#121212]">{children}</h2>{sub && <p className="mt-1 font-body text-sm text-[#666666]">{sub}</p>}</div>
      {badge}
    </div>
  );
}

export function UaePassCard({ customer, onChanged }) {
  const up = customer.uaepass;
  const [busy, setBusy] = useState(false);
  const unlink = async () => {
    if (!window.confirm('Unlink UAE PASS from this account?')) return;
    setBusy(true);
    try { onChanged(await uaepassUnlink()); toast.success('UAE PASS unlinked.'); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  if (!up) {
    return (
      <div data-testid="uaepass-card" className={`${CARD} border-[#121212]/15 bg-[linear-gradient(135deg,#FFFFFF_0%,#F5F5F3_100%)]`}>
        <Title sub="Confirm your identity once with the UAE national digital ID. Your verified name, Emirates ID, nationality and mobile are stored on your account so you never re-enter or re-upload them for any booking across the Eurogulf ecosystem.">Verify with UAE PASS</Title>
        <UaePassButton testid="account-link-uaepass" label="Verify with UAE PASS" intent="link" accountType={customer.account_type} />
        <p className="mt-3 flex items-center gap-2 font-body text-xs text-[#888]"><ShieldAlert className="w-3.5 h-3.5" /> Not verified yet</p>
      </div>
    );
  }
  const rows = [['Full name', up.fullname_en], ['Arabic name', up.fullname_ar], ['Emirates ID', up.idn_masked], ['Nationality', up.nationality_en], ['Gender', up.gender], ['Mobile', up.mobile], ['UAE PASS email', up.email]];
  return (
    <div data-testid="uaepass-card" className={CARD}>
      <Title sub="Identity details come from UAE PASS and are locked. They are reused for every booking and quote."
        badge={<span data-testid="uaepass-verified-badge" className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 font-heading text-[11px] font-bold uppercase tracking-wider ${up.verified ? 'bg-green-50 text-green-700 border-green-200' : 'bg-[#FFF4ED] text-[#EE5A01] border-[#EE5A01]/30'}`}><ShieldCheck className="w-3.5 h-3.5" /> {up.verified ? 'UAE PASS verified' : 'UAE PASS basic'}</span>}>
        UAE PASS identity
      </Title>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {rows.filter(([, v]) => v).map(([k, v]) => (<div key={k} className="flex justify-between gap-3 border-b border-black/5 py-2 font-body text-sm"><dt className="text-[#888]">{k}</dt><dd className="font-medium text-[#121212] text-right truncate" dir="ltr">{v}</dd></div>))}
      </dl>
      {!up.verified && <p className="mt-4 font-body text-xs text-[#7A2E00] bg-[#FFF4ED] border border-[#EE5A01]/30 rounded-lg p-3">Your UAE PASS account is a basic (SOP1) profile. Upgrade it in the UAE PASS app with your Emirates ID to unlock full verification.</p>}
      <button type="button" data-testid="uaepass-unlink" onClick={unlink} disabled={busy} className="mt-5 font-heading font-bold text-xs text-[#888] hover:text-red-600 disabled:opacity-50">Unlink UAE PASS</button>
    </div>
  );
}

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function UploadForm({ types, onUploaded }) {
  const [docType, setDocType] = useState('driving_licence');
  const [expiry, setExpiry] = useState('');
  const [file, setFile] = useState(null);
  const [state, setState] = useState({ error: '', busy: false });
  const inputRef = useRef(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setState({ error: '', busy: true });
    const fd = new FormData();
    fd.append('file', file); fd.append('doc_type', docType); fd.append('expiry_date', expiry);
    try {
      onUploaded(await uploadDocument(fd));
      setFile(null); setExpiry(''); if (inputRef.current) inputRef.current.value = '';
      setState({ error: '', busy: false });
      toast.success('Document saved to your account.');
    } catch (err) { setState({ error: apiError(err), busy: false }); }
  };
  return (
    <form onSubmit={submit} data-testid="document-upload-form" className="mt-5 rounded-xl border border-dashed border-black/15 p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label htmlFor="doc-type" className={LABEL_CLS}>Document type</label>
          <select id="doc-type" data-testid="document-type" value={docType} onChange={(e) => setDocType(e.target.value)} className={`${INPUT_CLS} w-full px-3 font-body text-sm border`}>
            {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><label htmlFor="doc-expiry" className={LABEL_CLS}>Expiry date (optional)</label><Input id="doc-expiry" data-testid="document-expiry" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className={INPUT_CLS} /></div>
      </div>
      <div><label htmlFor="doc-file" className={LABEL_CLS}>File (PDF, JPG, PNG or WEBP · max 8 MB)</label>
        <input ref={inputRef} id="doc-file" data-testid="document-file" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full font-body text-sm text-[#666] file:mr-3 file:rounded-full file:border-0 file:bg-[#F5F5F3] file:px-4 file:py-2 file:font-heading file:font-bold file:text-xs file:text-[#121212] hover:file:bg-[#FFF4ED]" /></div>
      {state.error && <p data-testid="document-error" role="alert" className={ERROR_CLS}>{state.error}</p>}
      <button type="submit" data-testid="document-upload-submit" disabled={!file || state.busy} className="btn-secondary disabled:opacity-50"><Upload className="w-4 h-4" /> {state.busy ? 'Uploading…' : 'Upload document'}</button>
    </form>
  );
}

export function DocumentsCard({ customer }) {
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState({});
  const [error, setError] = useState('');
  useEffect(() => { listDocuments().then((d) => { setDocs(d.documents); setTypes(d.types); }).catch((err) => setError(apiError(err))); }, []);
  const remove = async (id) => {
    if (!window.confirm('Remove this document?')) return;
    try { await deleteDocument(id); setDocs((p) => p.filter((d) => d.id !== id)); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div data-testid="documents-card" className={CARD}>
      <Title sub="Store your licence, passport, visa or trade licence once — our team uses them for every booking so you never re-send them.">My documents</Title>
      {customer.uaepass?.verified && <p data-testid="documents-uaepass-note" className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 p-3 font-body text-xs text-green-700"><ShieldCheck className="w-4 h-4" /> Your identity is verified by UAE PASS — no Emirates ID upload needed.</p>}
      {error && <p className={ERROR_CLS}>{error}</p>}
      {docs.length === 0 ? <p data-testid="documents-empty" className="font-body text-sm text-[#888]">No documents yet.</p> : (
        <ul data-testid="documents-list" className="divide-y divide-black/5">
          {docs.map((d) => (
            <li key={d.id} data-testid={`document-row-${d.id}`} className="flex items-center gap-3 py-3">
              <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-md bg-[#F5F5F3] text-[#EE5A01]"><FileText className="w-4 h-4" /></span>
              <span className="flex-1 min-w-0"><span className="block font-heading font-bold text-sm text-[#121212] truncate">{d.doc_label}</span><span className="block font-body text-xs text-[#888] truncate">{d.filename} · {fmtSize(d.size)}{d.expiry_date ? ` · expires ${d.expiry_date}` : ''}</span></span>
              <a href={documentUrl(d.id)} target="_blank" rel="noopener noreferrer" data-testid={`document-view-${d.id}`} className="p-2 text-[#666] hover:text-[#EE5A01]" title="View"><ExternalLink className="w-4 h-4" /></a>
              <button type="button" data-testid={`document-delete-${d.id}`} onClick={() => remove(d.id)} className="p-2 text-[#666] hover:text-red-600" title="Remove"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
      {Object.keys(types).length > 0 && <UploadForm types={types} onUploaded={(d) => setDocs((p) => [d, ...p])} />}
    </div>
  );
}
